import axios from 'axios';
import { API_BASE_URL, WEB_API_BASE_URL } from '../config/config';
import authService from './authService';
import { handleSessionExpired } from '../utils/sessionExpiredHandler';

interface Ticket {
  uid: string;
  formName: string;
  status: string;
  createdTime: string;
  assignedRole?: string;
  assigned_to?: string;
  assignedTo?: string;
  // Legacy fields for backwards compatibility
  form_name?: string;
  form_number?: string;
  customer_name?: string;
  customer_number?: string;
  commercial_unit?: string;
  sales_region?: string;
  region?: string;
  province?: string;
  city?: string;
  route_name?: string;
  current_user_jobpositionuid?: string;
  current_user_name?: string;
  form_status?: string;
  created_time?: string;
  server_modified_time?: string;
}

interface TicketResponse {
  success: boolean;
  tickets: Ticket[];
  message?: string;
}

class TicketService {
  private customerApiUrl = API_BASE_URL;
  private salesApiUrl = WEB_API_BASE_URL;

  async getTickets(limit?: number, tabStatus?: 'Open' | 'Closed'): Promise<TicketResponse> {
    try {
      const userType = authService.getUserType();
      let token: string | null;

      if (userType === 'sales') {
        // For sales users, try Web API token first, fallback to MobileService token
        token = authService.getWebApiToken() || localStorage.getItem('authToken');
      } else {
        // For customers, use MobileService token
        token = localStorage.getItem('authToken');
      }

      if (!token) {
        throw new Error('No authentication token found');
      }

      let apiUrl: string;
      let endpoint: string;
      let requestBody: any;

      if (userType === 'customer') {
        // For customers, use MobileService CustomerPortal API
        apiUrl = this.customerApiUrl;
        endpoint = '/CustomerPortal/tickets';

        const customerData = authService.getCustomerData();
        const customerNumber = customerData?.code || customerData?.Code ||
                              customerData?.number || customerData?.Number ||
                              customerData?.customerNumber || customerData?.CustomerNumber;

        if (!customerNumber) {
          console.error('Customer data:', customerData);
          throw new Error('Customer number not found in stored data');
        }

        requestBody = {
          customerNumber: customerNumber,
          TabStatus: tabStatus || 'Open' // CRITICAL: Add TabStatus parameter (matches Angular)
        };
      } else {
        // For sales users, use Web API service_task endpoint
        apiUrl = this.salesApiUrl;
        endpoint = '/ServiceTask/GetServiceTasks';

        requestBody = {
          MaximumRows: limit || 50,
          StartRowIndex: 0,
          SortExpression: 'server_modified_time DESC',
          StartDate: null,
          EndDate: null,
          FormName: '',
          FormNumber: '',
          TabStatus: tabStatus || 'Open' // CRITICAL: Add TabStatus parameter (matches Angular)
        };
      }

      const response = await axios.post(
        `${apiUrl}${endpoint}`,
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Normalize response format for different APIs
      if (userType === 'customer') {
        return response.data; // CustomerPortal API already has correct format
      } else {
        // Transform Web API response to match expected format
        const webApiData = response.data;
        return {
          success: webApiData.Status?.Type === 1,
          tickets: webApiData.Data?.ServiceTasks?.lstServiceTasks || [],
          message: webApiData.Status?.Msg || 'Data fetched successfully'
        };
      }
    } catch (error: any) {
      console.error('Failed to fetch tickets:', error);

      if (error.response?.status === 401) {
        handleSessionExpired();
        return { success: false, tickets: [], message: 'Session expired' };
      }

      throw new Error(error.response?.data?.message || 'Failed to fetch tickets');
    }
  }

  async getRecentTickets(): Promise<TicketResponse> {
    return this.getTickets(3, 'Open'); // Get first 3 open tickets for dashboard
  }

  async getAllTickets(tabStatus?: 'Open' | 'Closed'): Promise<TicketResponse> {
    return this.getTickets(undefined, tabStatus || 'Open'); // Get all tickets for history page
  }

  /**
   * Get My Complaints with tabs and tickets in single API call
   */
  async getMyComplaints(): Promise<any> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const customerData = localStorage.getItem('customerData');
      let customerNumber = '';

      if (customerData) {
        try {
          const customer = JSON.parse(customerData);
          customerNumber = customer.code || customer.Code ||
                          customer.number || customer.Number ||
                          customer.customerNumber || customer.CustomerNumber;
        } catch (e) {
          console.error('Error parsing customer data:', e);
        }
      }

      // Also try direct localStorage keys
      if (!customerNumber) {
        customerNumber = localStorage.getItem('customerCode') ||
                        localStorage.getItem('customerNumber') || '';
      }

      if (!customerNumber) {
        throw new Error('Customer number not found');
      }

      const requestBody = {
        customerNumber: customerNumber
      };

      console.log('🔍 Calling My-Complaints API with:', requestBody);

      const response = await axios.post(
        `${this.customerApiUrl}/CustomerPortal/My-Complaints`,
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('📦 My-Complaints API response:', response.data);

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch My Complaints:', error);

      if (error.response?.status === 401) {
        handleSessionExpired();
        return { success: false, tabs: [], message: 'Session expired' };
      }

      throw new Error(error.response?.data?.message || 'Failed to fetch complaints');
    }
  }
}

export default new TicketService();